import React, { useState } from 'react';
import T from 'i18n';
import { FilterListOutlined } from '@mui/icons-material';
import { Avatar, Button, Collapse, Grid, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import MatchesContainer from './MatchesContainer';
import SuggestedQuestionsCard from './SuggestedQuestionsCard';
import DocumentsContainer from './DocumentsContainer';
import { removeFormatSuffix } from './utils/formatUtils';

const useStyles = makeStyles({
  matchesDisplayContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '1rem 1rem 1rem 0',
  },
  matchesDisplayContainerAvatar: {
    width: 28,
    height: 28,
    margin: '0.5rem',
  },
  matchesDisplayContainerTopicSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  matchesDisplayContainerTopicText: {
    color: '#777',
    fontWeight: 700,
    marginRight: '1rem',
  },
  matchesResponseSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '1rem 1rem 1rem 1rem',
    width: '98%',
  },
  matchesResponseSectionAvatar: {
    width: 28,
    height: 28,
    margin: '0.5rem',
    backgroundColor: '#85A0F1',
  },
  matchesResponseSectionText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  matchesResponseSectionChip: {
    borderColor: '#85A0F1',
    marginLeft: '1rem',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
  },
  matchesResponseSectionHeaderRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
});

const searchingTextStyle = {
  animation: `blinker 1s linear infinite`,
  fontWeight: 600,
  color: '#0000007a',
};

const SearchMatchesDisplayContainer = ({ matchesData, fetchMatchDocumentsPending }: any) => {
  const classes = useStyles();
  const containsMatches = matchesData?.matches?.length > 0;
  const containsDocuments = matchesData?.documents?.length > 0;

  return (
    <>
      <Grid item lg={12} style={{ flex: 1 }}>
        <div className={classes.matchesDisplayContainer} style={{ flex: 1 }}>
          <div style={{ marginLeft: '1rem', flex: 1 }}>
            {!(matchesData.matches && matchesData.matches.length > 0) && (
              <div className={classes.matchesDisplayContainerTopicSection} style={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  className={classes.matchesDisplayContainerTopicText}
                  style={{
                    fontSize: '13px',
                    padding: '5px 15px',
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      color: '#777777',
                      backgroundColor: '#ffffff',
                      borderRadius: '5px',
                      padding: '15px',
                    }}
                  >
                    {(removeFormatSuffix(matchesData.phrase) || '')
                      .split('\n')
                      .map((line: string, idx: number) => (
                        <React.Fragment key={idx}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                  </div>
                </Typography>
              </div>
            )}

            {((matchesData.location && matchesData.location.length > 0) ||
              (matchesData.files && matchesData.files.length > 0)) && (
              <Tooltip
                placement="top"
                title={
                  <>
                    {matchesData.location.length > 0 && !matchesData.isCompare && (
                      <span>
                        <b>{T.translate('case.bundles')}</b> :{' '}
                        {matchesData.location.map((loc: any) => loc.name).join(', ')}{' '}
                      </span>
                    )}
                    {matchesData.files.length > 0 && (
                      <span>
                        <b>{T.translate('case.files')}</b> :{' '}
                        {matchesData.files.map((bm: any) => bm.id).join(', ')}
                      </span>
                    )}
                  </>
                }
                arrow
              >
                <div style={{ marginTop: '0.5rem' }}>
                  <FilterListOutlined fontSize="small" sx={{ mr: '0.5rem', mb: '0.2rem' }} />
                  {matchesData.location.length > 0 && !matchesData.isCompare && (
                    <span>
                      <b>{T.translate('case.bundles')}</b> : {matchesData.location[0].name}
                      {matchesData.location.length > 1
                        ? ` & ${matchesData.location.length - 1} more ...`
                        : ''}{' '}
                    </span>
                  )}
                  {matchesData.files && matchesData.files.length > 0 && (
                    <span>
                      <b>{T.translate('case.files')}</b> : {matchesData.files[0].id}
                      {matchesData.files.length > 1
                        ? ` & ${matchesData.files.length - 1} more ...`
                        : ''}
                    </span>
                  )}
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      </Grid>
      {(matchesData.matches || matchesData.documents || fetchMatchDocumentsPending) && (
        <>
          <Grid item xs={12}>
            <div className={classes.matchesResponseSection}>
              <div className={classes.matchesResponseSectionHeaderRow}>
                <Avatar className={classes.matchesResponseSectionAvatar}>
                  <Typography>{T.translate('case.tvAvatarText')}</Typography>
                </Avatar>
                {fetchMatchDocumentsPending && !matchesData.matches && !matchesData.documents && (
                  <div>
                    <Typography sx={{ fontWeight: 600, color: '#777777' }}>
                      {T.translate('case.trialview')}
                    </Typography>
                    <Typography style={searchingTextStyle}>
                      {T.translate('case.searching')}
                    </Typography>
                  </div>
                )}
                {(matchesData.matches || matchesData.documents) && (
                  <div className={classes.matchesResponseSectionText}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '14px',
                        ...((containsMatches || containsDocuments) && {
                          fontWeight: 600,
                          color: '#777777',
                        }),
                      }}
                    >
                      {containsMatches
                        ? T.translate('case.matchesFoundMsg', {
                            matchesCount: matchesData.matches.length,
                          })
                        : containsDocuments
                          ? T.translate('case.docsFoundMsg', {
                              docsCount: matchesData.documents.length,
                            })
                          : T.translate('case.noMatchesFoundMsg')}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </Grid>
        </>
      )}
    </>
  );
};

export default SearchMatchesDisplayContainer;
